import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SpecData, DepartmentSpec, ServiceDocsData, TimeOfDay, Transportation } from 'src/models';
import { ViewMode } from 'src/enums';
import { BlankDeptSpec, BlankSpecs, BlankServiceDocsSpecs } from 'src/models/Specs/TestSpecs';
import { IStoreState } from 'src/store';
import * as acBase from 'src/store/states/GenericActionCreator';
import { AxiosError } from 'axios';


export interface ISpecsState {
    specs?: SpecData;
    deptSpecs?: DepartmentSpec;
    serviceDocsSpecs?: ServiceDocsData;
}

export const initialState: ISpecsState = {
    specs: BlankSpecs,
    serviceDocsSpecs: BlankServiceDocsSpecs,
};

export const SpecsSlice = createSlice({
    name: 'Specs',
    initialState: initialState,
    reducers: {
        getSpecs: (state, action: PayloadAction<SpecData>) => {
            state.specs = action.payload;
        },
        getDeptSpecs: (state, action: PayloadAction<DepartmentSpec>) => {
            state.deptSpecs = action.payload;
        },
        getServiceDocsSpecs: (state, action: PayloadAction<ServiceDocsData>) => {
            state.serviceDocsSpecs = action.payload;
        },
        gmDealerEligibility: (state, action: PayloadAction<{gmDealerCanEarn: string, gmDealerCanRedeem: string}>) => {
            state.specs!.gmDealerCanRedeem = action.payload.gmDealerCanRedeem;
            state.specs!.gmDealerCanEarn = action.payload.gmDealerCanEarn;
        },
        toggleLangLits: (state, action: PayloadAction<SpecData>) => {
            state.specs = action.payload;
        },
    },
});

export const {
    getSpecs,
    getDeptSpecs,
    getServiceDocsSpecs,
    gmDealerEligibility,
    toggleLangLits,
} = SpecsSlice.actions;

export default SpecsSlice.reducer;

export function GetSpecs(specProfile: string, store: string, branch: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const logId = getState().Framework.logId;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        const viewServiceHistFlag = getState().Framework.viewServiceHistFlag;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist || viewServiceHistFlag) {
            callingProgram = 'OSD';
        }
        const reqData = {
            functionFlag: 'S',
            profileNum: specProfile,
            store: store,
            branch: branch,
            callingProgram: callingProgram,
            logId: logId,
        };
        const response = await acBase.eraWebServiceCall<SpecData>('SVPController', 'GetSpecs', reqData, store, branch);
        if (!response || (response as AxiosError).isAxiosError) {
            return;
        }
        dispatch(getSpecs({ ...response }));
    };
}

export function GetDeptSpecs() {
    // Now go though each selected job and change the deaprtment as needed.
    // If there are no selected jobs then default department based on the specs
    return (dispatch, getState: () => IStoreState) => {
        const CurrAppt = { ...getState().Appointment.Appointment };
        const CurrSpecs = { ...getState().Specs };
        let deptSpecs: DepartmentSpec | undefined = BlankDeptSpec;
        if (CurrSpecs.specs) {
            switch (CurrAppt.department) {
                case 'B':
                    deptSpecs = CurrSpecs.specs.bdySpecs;
                    break;
                case 'P':
                    deptSpecs = CurrSpecs.specs.pdiSpecs;
                    break;
                case 'Q':
                    deptSpecs = CurrSpecs.specs.ossSpecs;
                    break;
                default:
                    switch (CurrSpecs.specs?.departmentFlag) {
                        case 'B':
                            deptSpecs = CurrSpecs.specs.bdySpecs;
                            break;
                        case 'P':
                            deptSpecs = CurrSpecs.specs.pdiSpecs;
                            break;
                        case 'Q':
                            deptSpecs = CurrSpecs.specs.ossSpecs;
                            break;
                        default:
                            deptSpecs = CurrSpecs.specs.svcSpecs;
                    }
                    break;
            }
        }
        if (deptSpecs === undefined) {
            deptSpecs = BlankDeptSpec;
        }

        dispatch(getDeptSpecs(deptSpecs));
    };
}

export function GetServiceDocsSpecs(store: string, branch: string) {
    // Read Online Service Documents specs for ServiceDocs.
    return async (dispatch, getState) => {
        const logId = getState().Framework.logId;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        const serviceDocsFlag = getState().Framework.serviceDocsFlag;
        const viewServiceHistFlag = getState().Framework.viewServiceHistFlag;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist || serviceDocsFlag || viewServiceHistFlag) {
            callingProgram = 'OSD';
        }
        const reqData = {
            functionFlag: 'SD',
            logId: logId,
            callingProgram: callingProgram,
        };
        const response = await acBase.eraWebServiceCall<ServiceDocsData>('SVPController', 'GetSpecs', reqData, store, branch);

        dispatch(getServiceDocsSpecs(response));
    };
}

/**
 * Swaps the spec's display literals to the requested language and refreshes the current deptSpec.
 * @param language Only 'fr-CA' selects French; every other value falls through to English.
 */
export function ToggleLangLits(language: string) {
    return (dispatch, getState: () => IStoreState) => {
        const Specs = getState().Specs.specs;
        let response = Specs;

        if (response === undefined) {
            response = BlankSpecs;
        }
        let svcSpecs = response.svcSpecs;
        let bdySpecs = response.bdySpecs;
        let ossSpecs = response.ossSpecs;
        // Record which departments the dealer actually has before the blank substitution below,
        // so the substitute never gets written back onto the spec as a real department.
        const hasSvcSpecs = response.svcSpecs !== undefined;
        const hasBdySpecs = response.bdySpecs !== undefined;
        const hasOssSpecs = response.ossSpecs !== undefined;
        if (svcSpecs === undefined) {
            svcSpecs = BlankDeptSpec;
        }
        if (bdySpecs === undefined) {
            bdySpecs = BlankDeptSpec;
        }
        if (ossSpecs === undefined) {
            ossSpecs = BlankDeptSpec;
        }

        if (language === 'fr-CA') {
            response = { ...response, mobileName: response.FRMobileName };
            response = { ...response, titleTag: response.FRTitleTag };
            response = { ...response, schApptMsg: response.FRSchedAppt };
            response = { ...response, yourAcctMsg: response.FRYourAcct };
            response = { ...response, checkRepairMsg: response.FRCheckRepair };
            response = { ...response, emptyCartMsg: response.FREmptyCart };
            response = { ...response, textOptInMsg: response.FRTextOptIn };
            response = { ...response, maintSchTabMsg: response.FRMaintSchTab };
            response = { ...response, indSVCsTabMsg: response.FRIndSVCsTab };
            response = { ...response, writeYourOwnTabMsg: response.FRWriteYourOwnTab };
            response = { ...response, skipSVCMsg: response.FRSkipSVCMsg };
            response = { ...response, oemServices: response.FRoemServices };
            response = { ...response, userDefServices: response.FRuserDefServices };
            response = { ...response, weOweMsg: response.FRweOweMsg };
            response = { ...response, prevRecomMsg: response.FRprevRecomMsg };
            response = { ...response, recallGreetMsg: response.FRrecallGreetMsg };
            response = { ...response, diagnToolMsg: response.FRdiagnToolMsg };
            response = { ...response, recallMsg: response.FRrecallMsg };
            svcSpecs = { ...svcSpecs, maintMenuDisc: svcSpecs.maintMenuDiscFR };
            svcSpecs = { ...svcSpecs, requireConsMsg: svcSpecs.requireConsMsgFR };
            svcSpecs = { ...svcSpecs, confMessage: svcSpecs.confMessageFR };
            svcSpecs = { ...svcSpecs, disclaimerMsg: svcSpecs.disclaimerMsgFR };
            svcSpecs = { ...svcSpecs, advisorOption: svcSpecs.advisorOptionFR };
            svcSpecs = { ...svcSpecs, addtnlInfo: svcSpecs.addtnlInfoFR };
            let timeOfDayA = response.svcSpecs?.timeOfDay;
            let timeOfDayCopy: TimeOfDay[] = [];
            timeOfDayA?.forEach((time) => {
                timeOfDayCopy.push({
                    title: time.titleFR,
                    titleEN: time.titleEN,
                    titleFR: time.titleFR,
                    startTime: time.startTime,
                    endTime: time.endTime,
                    default: time.default});
            });
            svcSpecs = { ...svcSpecs, timeOfDay: timeOfDayCopy };
            let transA = response.svcSpecs?.Transportation;
            let transCopy: Transportation[] = [];
            transA?.forEach((transA) => {
                transCopy.push({
                    type: transA.type,
                    title: transA.titleFR,
                    titleEN: transA.titleEN,
                    titleFR: transA.titleFR,
                    information: transA.informationFR,
                    informationEN: transA.informationEN,
                    informationFR: transA.informationFR,
                    description: transA.descriptionFR,
                    descriptionEN: transA.descriptionEN,
                    descriptionFR: transA.descriptionFR,
                    defaultFlag: transA.defaultFlag,
                });
            });
            svcSpecs = { ...svcSpecs, Transportation: transCopy };
            svcSpecs = { ...svcSpecs, dmsWebTitle: svcSpecs.dmsWebTitleFR };
            svcSpecs = { ...svcSpecs, dmsNotStarted: svcSpecs.dmsNotStartedFR };
            svcSpecs = { ...svcSpecs, dmsInProgress: svcSpecs.dmsInProgressFR };
            svcSpecs = { ...svcSpecs, dmsComplete: svcSpecs.dmsCompleteFR };
            bdySpecs = { ...bdySpecs, maintMenuDisc: bdySpecs.maintMenuDiscFR };
            bdySpecs = { ...bdySpecs, requireConsMsg: bdySpecs.requireConsMsgFR };
            bdySpecs = { ...bdySpecs, confMessage: bdySpecs.confMessageFR };
            bdySpecs = { ...bdySpecs, disclaimerMsg: bdySpecs.disclaimerMsgFR };
            bdySpecs = { ...bdySpecs, advisorOption: bdySpecs.advisorOptionFR };
            bdySpecs = { ...bdySpecs, addtnlInfo: bdySpecs.addtnlInfoFR };
            timeOfDayA = response.bdySpecs?.timeOfDay;
            timeOfDayCopy = [];
            timeOfDayA?.forEach((time) => {
                timeOfDayCopy.push({
                    title: time.titleFR,
                    titleEN: time.titleEN,
                    titleFR: time.titleFR,
                    startTime: time.startTime,
                    endTime: time.endTime,
                    default: time.default});
            });
            bdySpecs = { ...bdySpecs, timeOfDay: timeOfDayCopy };
            transA = response.bdySpecs?.Transportation;
            transCopy = [];
            transA?.forEach((transA) => {
                transCopy.push({
                    type: transA.type,
                    title: transA.titleFR,
                    titleEN: transA.titleEN,
                    titleFR: transA.titleFR,
                    information: transA.informationFR,
                    informationEN: transA.informationEN,
                    informationFR: transA.informationFR,
                    description: transA.descriptionFR,
                    descriptionEN: transA.descriptionEN,
                    descriptionFR: transA.descriptionFR,
                    defaultFlag: transA.defaultFlag,
                });
            });
            bdySpecs = { ...bdySpecs, Transportation: transCopy };
            bdySpecs = { ...bdySpecs, dmsWebTitle: bdySpecs.dmsWebTitleFR };
            bdySpecs = { ...bdySpecs, dmsNotStarted: bdySpecs.dmsNotStartedFR };
            bdySpecs = { ...bdySpecs, dmsInProgress: bdySpecs.dmsInProgressFR };
            bdySpecs = { ...bdySpecs, dmsComplete: bdySpecs.dmsCompleteFR };
            ossSpecs = { ...ossSpecs, maintMenuDisc: ossSpecs.maintMenuDiscFR };
            ossSpecs = { ...ossSpecs, requireConsMsg: ossSpecs.requireConsMsgFR };
            ossSpecs = { ...ossSpecs, confMessage: ossSpecs.confMessageFR };
            ossSpecs = { ...ossSpecs, disclaimerMsg: ossSpecs.disclaimerMsgFR };
            ossSpecs = { ...ossSpecs, advisorOption: ossSpecs.advisorOptionFR };
            ossSpecs = { ...ossSpecs, addtnlInfo: ossSpecs.addtnlInfoFR };
            timeOfDayA = response.ossSpecs?.timeOfDay;
            timeOfDayCopy = [];
            timeOfDayA?.forEach((time) => {
                timeOfDayCopy.push({
                    title: time.titleFR,
                    titleEN: time.titleEN,
                    titleFR: time.titleFR,
                    startTime: time.startTime,
                    endTime: time.endTime,
                    default: time.default});
            });
            ossSpecs = { ...ossSpecs, timeOfDay: timeOfDayCopy };
            transA = response.ossSpecs?.Transportation;
            transCopy = [];
            transA?.forEach((transA) => {
                transCopy.push({
                    type: transA.type,
                    title: transA.titleFR,
                    titleEN: transA.titleEN,
                    titleFR: transA.titleFR,
                    information: transA.informationFR,
                    informationEN: transA.informationEN,
                    informationFR: transA.informationFR,
                    description: transA.descriptionFR,
                    descriptionEN: transA.descriptionEN,
                    descriptionFR: transA.descriptionFR,
                    defaultFlag: transA.defaultFlag,
                });
            });
            ossSpecs = { ...ossSpecs, Transportation: transCopy };
            ossSpecs = { ...ossSpecs, dmsWebTitle: ossSpecs.dmsWebTitleFR };
            ossSpecs = { ...ossSpecs, dmsNotStarted: ossSpecs.dmsNotStartedFR };
            ossSpecs = { ...ossSpecs, dmsInProgress: ossSpecs.dmsInProgressFR };
            ossSpecs = { ...ossSpecs, dmsComplete: ossSpecs.dmsCompleteFR };
        } else {
            response = { ...response, mobileName: response.ENMobileName };
            response = { ...response, titleTag: response.ENTitleTag };
            response = { ...response, schApptMsg: response.ENSchedAppt };
            response = { ...response, yourAcctMsg: response.ENYourAcct };
            response = { ...response, checkRepairMsg: response.ENCheckRepair };
            response = { ...response, emptyCartMsg: response.ENEmptyCart };
            response = { ...response, textOptInMsg: response.ENTextOptIn };
            response = { ...response, maintSchTabMsg: response.ENMaintSchTab };
            response = { ...response, indSVCsTabMsg: response.ENIndSVCsTab };
            response = { ...response, writeYourOwnTabMsg: response.ENWriteYourOwnTab };
            response = { ...response, skipSVCMsg: response.ENSkipSVCMsg };
            response = { ...response, oemServices: response.ENoemServices };
            response = { ...response, userDefServices: response.ENuserDefServices };
            response = { ...response, weOweMsg: response.ENweOweMsg };
            response = { ...response, prevRecomMsg: response.ENprevRecomMsg };
            response = { ...response, recallGreetMsg: response.ENrecallGreetMsg };
            response = { ...response, diagnToolMsg: response.ENdiagnToolMsg };
            response = { ...response, recallMsg: response.ENrecallMsg };
            svcSpecs = { ...svcSpecs, maintMenuDisc: svcSpecs.maintMenuDiscEN };
            svcSpecs = { ...svcSpecs, requireConsMsg: svcSpecs.requireConsMsgEN };
            svcSpecs = { ...svcSpecs, confMessage: svcSpecs.confMessageEN };
            svcSpecs = { ...svcSpecs, disclaimerMsg: svcSpecs.disclaimerMsgEN };
            svcSpecs = { ...svcSpecs, advisorOption: svcSpecs.advisorOptionEN };
            svcSpecs = { ...svcSpecs, addtnlInfo: svcSpecs.addtnlInfoEN };
            let timeOfDayA = response.svcSpecs?.timeOfDay;
            let timeOfDayCopy: TimeOfDay[] = [];
            timeOfDayA?.forEach((time) => {
                timeOfDayCopy.push({
                    title: time.titleEN,
                    titleEN: time.titleEN,
                    titleFR: time.titleFR,
                    startTime: time.startTime,
                    endTime: time.endTime,
                    default: time.default});
            });
            svcSpecs = { ...svcSpecs, timeOfDay: timeOfDayCopy };
            let transA = response.svcSpecs?.Transportation;
            let transCopy: Transportation[] = [];
            transA?.forEach((transA) => {
                transCopy.push({
                    type: transA.type,
                    title: transA.titleEN,
                    titleEN: transA.titleEN,
                    titleFR: transA.titleFR,
                    information: transA.informationEN,
                    informationEN: transA.informationEN,
                    informationFR: transA.informationFR,
                    description: transA.descriptionEN,
                    descriptionEN: transA.descriptionEN,
                    descriptionFR: transA.descriptionFR,
                    defaultFlag: transA.defaultFlag,
                });
            });
            svcSpecs = { ...svcSpecs, Transportation: transCopy };
            svcSpecs = { ...svcSpecs, dmsWebTitle: svcSpecs.dmsWebTitleEN };
            svcSpecs = { ...svcSpecs, dmsNotStarted: svcSpecs.dmsNotStartedEN };
            svcSpecs = { ...svcSpecs, dmsInProgress: svcSpecs.dmsInProgressEN };
            svcSpecs = { ...svcSpecs, dmsComplete: svcSpecs.dmsCompleteEN };
            bdySpecs = { ...bdySpecs, maintMenuDisc: bdySpecs.maintMenuDiscEN };
            bdySpecs = { ...bdySpecs, requireConsMsg: bdySpecs.requireConsMsgEN };
            bdySpecs = { ...bdySpecs, confMessage: bdySpecs.confMessageEN };
            bdySpecs = { ...bdySpecs, disclaimerMsg: bdySpecs.disclaimerMsgEN };
            bdySpecs = { ...bdySpecs, advisorOption: bdySpecs.advisorOptionEN };
            bdySpecs = { ...bdySpecs, addtnlInfo: bdySpecs.addtnlInfoEN };
            timeOfDayA = response.bdySpecs?.timeOfDay;
            timeOfDayCopy = [];
            timeOfDayA?.forEach((time) => {
                timeOfDayCopy.push({
                    title: time.titleEN,
                    titleEN: time.titleEN,
                    titleFR: time.titleFR,
                    startTime: time.startTime,
                    endTime: time.endTime,
                    default: time.default});
            });
            bdySpecs = { ...bdySpecs, timeOfDay: timeOfDayCopy };
            transA = response.bdySpecs?.Transportation;
            transCopy = [];
            transA?.forEach((transA) => {
                transCopy.push({
                    type: transA.type,
                    title: transA.titleEN,
                    titleEN: transA.titleEN,
                    titleFR: transA.titleFR,
                    information: transA.informationEN,
                    informationEN: transA.informationEN,
                    informationFR: transA.informationFR,
                    description: transA.descriptionEN,
                    descriptionEN: transA.descriptionEN,
                    descriptionFR: transA.descriptionFR,
                    defaultFlag: transA.defaultFlag,
                });
            });
            bdySpecs = { ...bdySpecs, Transportation: transCopy };
            bdySpecs = { ...bdySpecs, dmsWebTitle: bdySpecs.dmsWebTitleEN };
            bdySpecs = { ...bdySpecs, dmsNotStarted: bdySpecs.dmsNotStartedEN };
            bdySpecs = { ...bdySpecs, dmsInProgress: bdySpecs.dmsInProgressEN };
            bdySpecs = { ...bdySpecs, dmsComplete: bdySpecs.dmsCompleteEN };
            ossSpecs = { ...ossSpecs, maintMenuDisc: ossSpecs.maintMenuDiscEN };
            ossSpecs = { ...ossSpecs, requireConsMsg: ossSpecs.requireConsMsgEN };
            ossSpecs = { ...ossSpecs, confMessage: ossSpecs.confMessageEN };
            ossSpecs = { ...ossSpecs, disclaimerMsg: ossSpecs.disclaimerMsgEN };
            ossSpecs = { ...ossSpecs, advisorOption: ossSpecs.advisorOptionEN };
            ossSpecs = { ...ossSpecs, addtnlInfo: ossSpecs.addtnlInfoEN };
            timeOfDayA = response.ossSpecs?.timeOfDay;
            timeOfDayCopy = [];
            timeOfDayA?.forEach((time) => {
                timeOfDayCopy.push({
                    title: time.titleEN,
                    titleEN: time.titleEN,
                    titleFR: time.titleFR,
                    startTime: time.startTime,
                    endTime: time.endTime,
                    default: time.default});
            });
            ossSpecs = { ...ossSpecs, timeOfDay: timeOfDayCopy };
            transA = response.ossSpecs?.Transportation;
            transCopy = [];
            transA?.forEach((transA) => {
                transCopy.push({
                    type: transA.type,
                    title: transA.titleEN,
                    titleEN: transA.titleEN,
                    titleFR: transA.titleFR,
                    information: transA.informationEN,
                    informationEN: transA.informationEN,
                    informationFR: transA.informationFR,
                    description: transA.descriptionEN,
                    descriptionEN: transA.descriptionEN,
                    descriptionFR: transA.descriptionFR,
                    defaultFlag: transA.defaultFlag,
                });
            });
            ossSpecs = { ...ossSpecs, Transportation: transCopy };
            ossSpecs = { ...ossSpecs, dmsWebTitle: ossSpecs.dmsWebTitleEN };
            ossSpecs = { ...ossSpecs, dmsNotStarted: ossSpecs.dmsNotStartedEN };
            ossSpecs = { ...ossSpecs, dmsInProgress: ossSpecs.dmsInProgressEN };
            ossSpecs = { ...ossSpecs, dmsComplete: ossSpecs.dmsCompleteEN };
        }

        // Only write a department back if the dealer had it configured. Writing unconditionally
        // persists the blank substitute above and fabricates departments the dealer doesn't have.
        if (hasSvcSpecs) {
            response = { ...response, svcSpecs: svcSpecs };
        }
        if (hasBdySpecs) {
            response = { ...response, bdySpecs: bdySpecs };
        }
        if (hasOssSpecs) {
            response = { ...response, ossSpecs: ossSpecs };
        }
        dispatch(toggleLangLits(response));
        // Need to update the deptSpec state values as well
        const CurrAppt = { ...getState().Appointment.Appointment };
        const CurrSpecs = response;
        let deptSpecs: DepartmentSpec | undefined = BlankDeptSpec;
        if (CurrSpecs) {
            switch (CurrAppt.department) {
                case 'B':
                    deptSpecs = CurrSpecs.bdySpecs;
                    break;
                case 'P':
                    deptSpecs = CurrSpecs.pdiSpecs;
                    break;
                case 'Q':
                    deptSpecs = CurrSpecs.ossSpecs;
                    break;
                default:
                    switch (CurrSpecs.departmentFlag) {
                        case 'B':
                            deptSpecs = CurrSpecs.bdySpecs;
                            break;
                        case 'P':
                            deptSpecs = CurrSpecs.pdiSpecs;
                            break;
                        case 'Q':
                            deptSpecs = CurrSpecs.ossSpecs;
                            break;
                        default:
                            deptSpecs = CurrSpecs.svcSpecs;
                    }
                    break;
            }
        }
        if (deptSpecs === undefined) {
            deptSpecs = BlankDeptSpec;
        }
        dispatch(getDeptSpecs(deptSpecs));
    };
}